import { __decorate } from "tslib";
import { MfsCollectionKind } from '@metaos/mfs-client';
import { ArgumentException, AssertionError, IllegalStateException, ValidationError } from '../errors';
import { mfsErrorHandler } from '../errors/mfsErrorHandler';
import { MAX_TAG_UNTAG_BATCH_SIZE, MFS_CARD_TYPE, MFS_TAG_CONTAINER_TYPE, MFS_TAG_TYPE, TAG_CONTAINER_ID, } from '../utility/constant/constants';
import { taggedRelationship, taggedRelationshipIn } from '../utility/constant/internalRelationships';
import { getMfsTag, getMfsTagByValue, getMfsTagOrThrowError, sanitizeTagValue, sanitizeTagValueForId, toTag, } from '../utility/taggingUtils';
import { logVerbose, logger } from '../utility/telemetry';
import { getMfsItem, getMfsItemOrThrowError, splitArrayToBatches } from '../utility/utils';
import { isTag } from '../validation/typeGuard/folderTypeGuard';
import { throwsIfContainsReservedProperties, throwsIfEmptyOrWhitespace } from '../validation/validation';
/** @inheritdoc */
export class TagClient {
    constructor(dataStore, cardPropertyUtils, configurationManager) {
        this.dataStore = dataStore;
        this.cardPropertyUtils = cardPropertyUtils;
        this.configurationManager = configurationManager;
    }
    /** Gets tagContainerId if it exists, else throws error. */
    get tagContainerIdDefined() {
        if (this.tagContainerId) {
            return this.tagContainerId;
        }
        throw new IllegalStateException('Tag Container has not been defined.');
    }
    /** Initialize tag container */
    async internalInit() {
        this.dataStore.on('bootItemsChanged', (eve) => {
            var _a;
            if (eve.key === TAG_CONTAINER_ID) {
                logger.logVerbose('bootItemsChanged', 'Boot item changed', { event: eve });
                this.tagContainerId = eve.currentValue ? (_a = this.dataStore.getMfsItem(eve.currentValue)) === null || _a === void 0 ? void 0 : _a.id : undefined;
            }
        });
        // Get boot item.
        this.tagContainerId = this.dataStore.getBootItem(TAG_CONTAINER_ID);
        if (!this.tagContainerId) {
            let itemId;
            try {
                //
                // If no boot item exists assume the tag container does not exist and create it.
                logger.logVerbose('internalInit', 'No Tag Container BootItem exists. Creating tag container');
                const mfsTag = await this.dataStore.createCollection({
                    label: MFS_TAG_CONTAINER_TYPE,
                    displayText: MFS_TAG_CONTAINER_TYPE,
                    labelId: TAG_CONTAINER_ID,
                    kind: MfsCollectionKind.OrderedList,
                });
                itemId = mfsTag.id;
            }
            catch (error) {
                // If create fails, check to see if tag container exists, but no boot item was created.
                // This would indicate a failure in a different initialization process.
                itemId = getMfsItemOrThrowError(this.dataStore, TAG_CONTAINER_ID, MFS_TAG_CONTAINER_TYPE).id;
                logger.logVerbose('internalInit', 'Tag Container BootItem did not exist, but found tag container item.', {
                    itemId,
                });
            }
            logger.logVerbose('internalInit', `Setting Tag Container BootItem.`, { itemId });
            this.dataStore.setBootItem(TAG_CONTAINER_ID, itemId);
        }
    }
    /**
     * Set up any tag related data in the pod if it does not exist.
     */
    static async initialize(datastore, cardPropertyUtils, configurationManager) {
        const tagClient = new TagClient(datastore, cardPropertyUtils, configurationManager);
        await tagClient.internalInit();
        return tagClient;
    }
    /** @inheritdoc */
    async tag(cardId, tagId) {
        const uniqueCardIds = this.validateAndEnumerateValues(cardId, 'cardId');
        const uniqueTagIds = this.validateAndEnumerateValues(tagId, 'tagId');
        const mfsTags = uniqueTagIds.map((t) => getMfsTagOrThrowError(this.dataStore, t));
        const tagBatches = splitArrayToBatches(mfsTags, MAX_TAG_UNTAG_BATCH_SIZE);
        const failedCardIds = [];
        let failedCardCount = 0;
        for (const cId of uniqueCardIds) {
            const mfsCard = getMfsItem(this.dataStore, cId, MFS_CARD_TYPE);
            if (!mfsCard) {
                logger.logVerbose('tag', `Card does not exist. Continuing with batch.`);
                failedCardCount++;
                continue;
            }
            let numberOfTags = 0;
            for (const tagBatch of tagBatches) {
                try {
                    const tagIds = tagBatch.map((tag) => tag.id);
                    this.dataStore.addRelatedItems(mfsCard.id, taggedRelationship, tagIds);
                    logger.logVerbose('tag', 'Tagged Card', { cardItemId: mfsCard.id, tagItemIds: tagIds });
                    numberOfTags += tagBatch.length;
                }
                catch (error) {
                    failedCardIds.push(mfsCard.id);
                    failedCardCount++;
                }
            }
            logger.logCustomMetric('TagsPerCardCount', 'Tagged Card', { card: mfsCard.id, count: numberOfTags }, 'tag');
            await this.cardPropertyUtils.refreshUpdatedAtTimeSafe(mfsCard);
        }
        // Update the status for used Tag if it's not marked as inUse previously
        for (const tag of mfsTags) {
            if (Array.from(this.dataStore.getRelatedItems(tag.id, taggedRelationshipIn)).length > 0) {
                await this.cardPropertyUtils.updateTagInUseStatus(tag, true);
            }
        }
        logger.logCustomMetric('CardTaggedCount', 'Card Tagged Total', { taggedCount: Math.abs(uniqueCardIds.length - failedCardCount) }, 'tag');
        if (failedCardCount > 0) {
            throw new AssertionError(`Failed to tag ${failedCardCount} Cards to due unexpectd error: ${failedCardIds.length > 0 ? `Cards ${failedCardIds.join(', ')} could not be tagged` : `No valid Card found`}. The rest Cards were tagged successfully.`);
        }
    }
    /** @inheritdoc */
    async untag(cardId, tagId) {
        const uniqueCardIds = this.validateAndEnumerateValues(cardId, 'cardId');
        const uniqueTagIds = this.validateAndEnumerateValues(tagId, 'tagId');
        const mfsTags = [];
        uniqueTagIds.forEach((tId) => {
            const mfsTag = getMfsTag(this.dataStore, tId);
            // Instead of throwing error, simply skip if tag not exist
            if (mfsTag) {
                mfsTags.push(mfsTag);
            }
            else {
                logger.logVerbose('untag', `Skip untagging as target Tag does not exist`, {
                    tagItemId: tId,
                });
            }
        });
        if (mfsTags.length === 0) {
            logger.logVerbose('untag', `No valid Tags found. Skip untagging Cards and terminate the call.`, {
                tagItemIds: uniqueTagIds,
            });
            return;
        }
        const tagBatches = splitArrayToBatches(mfsTags, MAX_TAG_UNTAG_BATCH_SIZE);
        const failedCardIds = [];
        let failedCardCount = 0;
        for (const cId of uniqueCardIds) {
            const mfsCard = getMfsItem(this.dataStore, cId, MFS_CARD_TYPE);
            if (!mfsCard) {
                logger.logVerbose('untag', `Card does not exist. Continuing with batch.`);
                failedCardCount++;
                continue;
            }
            let numberOfTags = 0;
            for (const tagBatch of tagBatches) {
                const tagIds = tagBatch.map((t) => t.id);
                try {
                    this.dataStore.removeRelatedItems(mfsCard.id, taggedRelationship, tagIds);
                    logger.logVerbose('untag', 'Untagged Card', { cardItemId: mfsCard.id, tagItemIds: tagIds });
                    numberOfTags += tagIds.length;
                }
                catch (error) {
                    // If the operation did not result in all tags being removed for the card, then store the error to throw later and keep untagging the rest of the cards.
                    const tagsIdsStillAttached = tagIds
                        .map((t) => { var _a; return (_a = this.findTagForCardId(mfsCard.id, t)) === null || _a === void 0 ? void 0 : _a.id; })
                        .filter((t) => t !== undefined);
                    if (tagsIdsStillAttached.length > 0) {
                        logger.logVerbose('untag', 'Failed to untag Card', {
                            cardItemId: mfsCard.id,
                            attachedTagItemIds: tagsIdsStillAttached,
                        });
                        failedCardIds.push(mfsCard.id);
                    }
                }
            }
            logger.logCustomMetric('UntagsPerCardCount', 'Untagged Card', { card: mfsCard.id, count: numberOfTags }, 'untag');
            await this.cardPropertyUtils.refreshUpdatedAtTimeSafe(mfsCard);
        }
        // Update the inUse status for unused Tag who has no other cards associated globally
        for (const tag of mfsTags) {
            if (Array.from(this.dataStore.getRelatedItems(tag.id, taggedRelationshipIn)).length === 0) {
                await this.cardPropertyUtils.updateTagInUseStatus(tag, false);
            }
        }
        logger.logCustomMetric('CardsUntaggedCount', 'Cards Untagged Total', { untaggedCount: Math.abs(uniqueCardIds.length - failedCardCount) }, 'untag');
        if (failedCardCount > 0) {
            throw new AssertionError(`Failed to untag ${failedCardCount} Cards to due unexpectd error: ${failedCardIds.length > 0 ? `Cards ${failedCardIds.join(', ')} could not be tagged` : `No valid Card found`}. The rest Cards were untagged successfully.`);
        }
    }
    /** @inheritdoc */
    async patchTag(tagId, tagPatch) {
        throwsIfEmptyOrWhitespace(tagId, 'tagId');
        throwsIfContainsReservedProperties(tagPatch);
        try {
            const retrievedTag = getMfsTagOrThrowError(this.dataStore, tagId);
            const partialTagToPatch = this.preparePatch(tagId, retrievedTag, tagPatch);
            if (!partialTagToPatch) {
                logger.logVerbose('patchTag', 'No differences found for Tag. Returning with no actions.', { tagItemId: tagId });
                return;
            }
            await this.dataStore.patchItem(retrievedTag.id, partialTagToPatch);
            if ('value' in partialTagToPatch) {
                const toFindPos = partialTagToPatch;
                const newPosition = this.findPositionInCollection(toFindPos, true);
                const element = Array.from(this.dataStore.getElementIdsOf(this.tagContainerIdDefined, retrievedTag.id))[0];
                if (newPosition && element) {
                    this.dataStore.moveItem(this.tagContainerIdDefined, { place: 'at', elementId: element.elementId }, newPosition);
                }
                else {
                    logger.logVerbose('patchTag', 'A new position or elementId could not be found for Tag.', {
                        tagItemId: retrievedTag.id,
                    });
                }
            }
            for (const taggedCard of this.dataStore.getRelatedItems(retrievedTag.id, taggedRelationshipIn)) {
                await this.cardPropertyUtils.refreshUpdatedAtTimeSafe(taggedCard);
            }
            logger.logVerbose('patchTag', 'Patched tag', { tagItemId: retrievedTag.id });
        }
        catch (error) {
            if (error instanceof ValidationError || error instanceof ArgumentException) {
                return Promise.reject(error);
            }
            return Promise.reject(mfsErrorHandler(`Failed to patch tag ${tagId} due to unexpected error.`, error));
        }
    }
    /** @inheritdoc */
    async deleteTag(tagId) {
        throwsIfEmptyOrWhitespace(tagId, 'tagId');
        try {
            const tag = getMfsTag(this.dataStore, tagId);
            if (!tag)
                return;
            // Remove the relationships ourselves instead of relying on deleteItem to remove the relationships because
            // deleteItem actually deletes the tag before removing the relationship and then we cannot retrieve the
            // tag for the event notification.
            await this.removeRelationships(tag.id, taggedRelationshipIn);
            this.dataStore.removeItemById(this.tagContainerIdDefined, tag === null || tag === void 0 ? void 0 : tag.id);
            logger.logVerbose('deleteTag', 'Deleting tag', { tagItemId: tag.id });
            return this.dataStore.deleteItem(tagId);
        }
        catch (error) {
            return Promise.reject(mfsErrorHandler(`Failed to delete tag due to unexpected error`, error));
        }
    }
    /** @inheritdoc */
    *getTags(query) {
        for (const mfsTag of this.getAllMfsTags()) {
            const tag = toTag(mfsTag);
            if (!query || !query.filter || query.filter(tag)) {
                yield tag;
            }
        }
    }
    /** @inheritdoc */
    async createTag(tag) {
        this.validateTag(tag);
        if (getMfsTagByValue(this.dataStore, tag.value) !== undefined) {
            throw new ValidationError(`Failed to create Tag when target Tag already exists.`);
        }
        try {
            const newMfsTag = this.toMfsTag(tag);
            const createdMfsTag = await this.dataStore.createItem(newMfsTag);
            const position = this.findPositionInCollection(createdMfsTag, false);
            if (position) {
                this.dataStore.insertItem(this.tagContainerIdDefined, createdMfsTag.id, position);
            }
            logger.logVerbose('createTag', 'Created tag', { tagItemId: createdMfsTag.id });
            return createdMfsTag.id;
        }
        catch (error) {
            return Promise.reject(mfsErrorHandler(`Failed to create tag due to unexpected error`, error));
        }
    }
    /** @inheritdoc */
    getTag(tagId) {
        throwsIfEmptyOrWhitespace(tagId, 'tagId');
        try {
            const mfsTag = getMfsTag(this.dataStore, tagId);
            if (!mfsTag)
                return undefined;
            logger.logVerbose('getTag', 'Retrieved tag', { tagItemId: mfsTag.id });
            return toTag(mfsTag);
        }
        catch (error) {
            throw mfsErrorHandler(`Failed to get tag due to unexpected error`, error);
        }
    }
    /** @inheritdoc */
    getTagByValue(tagValue) {
        throwsIfEmptyOrWhitespace(tagValue, 'tagValue');
        try {
            const mfsTag = getMfsTagByValue(this.dataStore, tagValue);
            if (!mfsTag)
                return undefined;
            logger.logVerbose('getTagByValue', 'Retrieved tag', { tagItemId: mfsTag.id });
            return toTag(mfsTag);
        }
        catch (error) {
            throw mfsErrorHandler(`Failed to get tag due to unexpected error`, error);
        }
    }
    /**
     * Validates and sanitizes the passed in Tag partial to use for patching.
     * @param tagId uniqueId of the Tag to be patched.
     * @param existingTag Current persisted Tag.
     * @param tagPatch Partial Tag properties to be updated.
     * @returns Validated and sanitized Partial Tag.
     */
    preparePatch(tagId, existingTag, tagPatch) {
        // Remove uniqueId if it got passed in.
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { uniqueId, ...restOfPatch } = tagPatch;
        const partialTagToPatch = {
            ...restOfPatch,
        };
        if (!('value' in partialTagToPatch))
            return partialTagToPatch;
        const newTagValue = tagPatch.value;
        if (!newTagValue) {
            throw new ArgumentException(`Failed to patch tag when the 'value' field is null or undefined.`);
        }
        this.validateTagValueLength(newTagValue);
        const sanatizedNewValue = sanitizeTagValue(newTagValue);
        if (sanatizedNewValue === existingTag.value) {
            // No update to value.
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const { value, ...patchMinusValue } = partialTagToPatch;
            logger.logVerbose('preparePatch', 'No update to value for Tag.', { tagItemId: tagId });
            return patchMinusValue;
        }
        const existingTagWithValue = getMfsTagByValue(this.dataStore, newTagValue);
        if (existingTagWithValue && existingTagWithValue.id !== tagId) {
            throw new ValidationError('The new Tag value provided already exists.');
        }
        return {
            ...partialTagToPatch,
            labelId: sanitizeTagValueForId(newTagValue),
            displayText: sanatizedNewValue,
            value: sanatizedNewValue,
        };
    }
    /** Performs a comparison between 2 MfsTag objects based on DisplayText respecting case insensitive */
    compareFunction(a, b) {
        //https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/Collator/Collator#options
        return a.displayText.localeCompare(b.displayText, undefined, { sensitivity: 'base' });
    }
    /** Finds the correct position where a Tag should be placed inside the general tag container */
    findPositionInCollection(newTag, isPatch, compareFunction) {
        if (!compareFunction) {
            compareFunction = this.compareFunction;
        }
        if (!this.tagContainerId) {
            throw new AssertionError('Tag Container undefined');
        }
        let position;
        let count = 0;
        for (const colItem of this.dataStore.getCollectionItems(this.tagContainerId)) {
            const currTag = colItem.item;
            if (newTag.displayText.toLocaleLowerCase() === currTag.displayText.toLocaleLowerCase()) {
                continue;
            }
            const compareResult = compareFunction(newTag, currTag);
            count++;
            if (compareResult >= 0) {
                position = { place: 'after', elementId: colItem.elementId };
            }
            else {
                position = { place: 'before', elementId: colItem.elementId };
                break;
            }
        }
        if (isPatch && count === 0) {
            logger.logVerbose('findPositionInCollection', 'No position in Tag Collection found for Tag', {
                tagItemId: newTag.id,
            });
            return undefined;
        }
        if (!position) {
            position = { place: 'last' };
        }
        return position;
    }
    /** Removes all occurrences of the specified relationship. */
    async removeRelationships(mfsItemId, relationship) {
        const relatedItems = Array.from(this.dataStore.getRelatedItems(mfsItemId, relationship));
        if (relatedItems.length === 0) {
            logger.logVerbose('removeRelationships', `No related items found for mfsItem for the relationshipType`, {
                itemId: mfsItemId,
                relationshipType: relationship.type,
            });
            return;
        }
        this.dataStore.removeRelatedItems(mfsItemId, relationship, relatedItems.map((i) => i.id));
        if (relationship.type === taggedRelationshipIn.type) {
            relatedItems.forEach(async (card) => {
                await this.cardPropertyUtils.refreshUpdatedAtTimeSafe(card);
            });
        }
    }
    /**
     * Gets all tags from MFS.
     */
    *getAllMfsTags() {
        for (const mfsTag of this.dataStore.getCollectionItems(this.tagContainerIdDefined)) {
            yield mfsTag.item;
        }
    }
    /**
     * Gets the Tag with the specified tag id if it exists and is 'tagged' to the specified Card.
     * @param mfscardId MFS ItemId of the Card to find the tag for.
     * @param tagValue Value of the tag to find.
     * @returns The MfsTag if it exists and is 'tagged' to the Card. returns undefined if there is no Tag with that value on the Card.
     */
    findTagForCardId(mfscardId, tagId) {
        for (const relItem of this.dataStore.getRelatedItems(mfscardId, taggedRelationship)) {
            if (relItem.id !== tagId)
                continue;
            logger.logVerbose('findTagForCardId', 'Found Tag for Card.', { tagItemId: tagId, cardItemId: mfscardId });
            return relItem;
        }
        logger.logVerbose('findTagForCardId', 'Could not find Tag for Card.', {
            tagItemId: tagId,
            cardItemId: mfscardId,
        });
        return undefined;
    }
    /** Converges types to array, validates input strings are not null, and returns an array of unique values. */
    validateAndEnumerateValues(value, propName) {
        const values = Array.isArray(value) ? value : [value];
        if (values.length === 0)
            throw new IllegalStateException(`No values for ${propName} were passed in.`);
        const uniqueValues = new Set();
        for (const val of values) {
            throwsIfEmptyOrWhitespace(val, propName);
            uniqueValues.add(val);
        }
        return Array.from(uniqueValues.values());
    }
    /** Helper function converting a Tag to an MfsTag object. */
    toMfsTag(tag) {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { uniqueId, ...theRest } = tag;
        return {
            ...theRest,
            label: MFS_TAG_TYPE,
            labelId: sanitizeTagValueForId(tag.value),
            displayText: sanitizeTagValue(tag.value),
            inUse: false,
        };
    }
    /** Checks if the provided tag definition is valid and throws error otherwise. */
    validateTag(tagInput) {
        if (!tagInput) {
            throw new ArgumentException('Provided Tag value cannot be null or undefined.');
        }
        if (!isTag(tagInput)) {
            throw new ArgumentException('Provided value is not defined as a valid Tag.');
        }
        throwsIfContainsReservedProperties(tagInput);
        throwsIfEmptyOrWhitespace(tagInput.value, 'tagValue');
        this.validateTagValueLength(tagInput.value);
    }
    /** Validates the tag value's length and throws if exceeds maximum allowed characters. */
    validateTagValueLength(tagValue) {
        const userTagMaxLength = this.configurationManager.getUserTagMaxLength();
        if (tagValue.length > userTagMaxLength) {
            throw new ValidationError(`Tag value cannot exceed maximum allowed length of ${userTagMaxLength} characters.`);
        }
    }
}
__decorate([
    logVerbose()
], TagClient.prototype, "preparePatch", null);
__decorate([
    logVerbose()
], TagClient.prototype, "findPositionInCollection", null);
__decorate([
    logVerbose()
], TagClient.prototype, "removeRelationships", null);
__decorate([
    logVerbose()
], TagClient.prototype, "getAllMfsTags", null);
__decorate([
    logVerbose()
], TagClient, "initialize", null);
//# sourceMappingURL=tagClient.js.map